import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useInventoryModal } from '../context/InventoryModalContext';
import { useDialog } from '../context/DialogContext';
import { useLocale } from '../context/LocaleContext';
import { apiService } from '../services/api';
import { progressService, ProgressItem, JourneyProgress } from '../services/progressService';
import { inventoryService, InventoryService } from '../services/inventoryService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import Header from '../components/Header';
import StepActions, { ActionItem } from '../components/StepActions';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { useRelocalizeStepActions } from '../hooks/useRelocalizeStepActions';
import { withShareButton } from '../utils/stepShareUtils';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';
import styles from '../styles/stepCommon.module.css';
import { STEP_UI } from '../config/uiStrings';
import { getStepActionLabel } from '../config/stepActionLabels';
import { getStepActionDescription } from '../config/stepActionDescriptions';
import {
  getCongratsBody,
  getCongratsHeading,
  getGuidanceModalTitle,
  getNextStepNavLabel,
  getPrevStepNavLabel,
  getRefreshPageLabel,
  getStepBreadcrumbs,
  getStepLoadingMessage,
  getStepPageQuote,
  getStepPageTitle,
  getVideoGuideTitle,
} from '../config/stepPageCopy';

interface Step4Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

interface BreadcrumbItem {
  label: string;
  path: string;
  active: boolean;
}

// Inventory types configuration — labels resolve through t() at render time so locale switches apply
const INVENTORY_TYPES = [
  { key: 'resentments', labelKey: 'step.4.inventory.type.resentments' },
  { key: 'fears', labelKey: 'step.4.inventory.type.fears' },
  { key: 'harms', labelKey: 'step.4.inventory.type.harms' }
];

// Helper functions
function getDefaultItem(type: string): any {
  switch (type) {
    case 'resentments':
      return { who: '', why: '', feeling: '', impacts: [], myPart: '' };
    case 'fears':
      return { fear: '', why: '', impacts: [] };
    case 'harms':
      return { whoHarmed: '', how: '', why: '', defects: [], alternative: '' };
    default:
      return {};
  }
}

function isComplete(type: string, item: any): boolean {
  if (!item) return false;
  
  switch (type) {
    case 'resentments':
      return item.who && item.why && item.feeling && Array.isArray(item.impacts) && item.impacts.length > 0 && item.myPart;
    case 'fears':
      return item.fear && item.why && Array.isArray(item.impacts) && item.impacts.length > 0;
    case 'harms':
      return item.whoHarmed && item.how && item.why && Array.isArray(item.defects) && item.defects.length > 0 && item.alternative;
    default:
      return false;
  }
}

function formatVersionDate(date: Date) {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Type definitions
type InventoryData = Record<string, any[]>;
type VersionMap = Record<string, string[]>;
type CurrentVersionMap = Record<string, string>;
type VersionDataMap = Record<string, any[]>;
type InventoryTypeKey = 'resentments' | 'fears' | 'harms';

const INVENTORY_TYPE_KEYS: InventoryTypeKey[] = ['resentments', 'fears', 'harms'];

function getDefaultVersionKey(type: InventoryTypeKey): string {
  return `${type}-current`;
}

function resolveInventoryVersionKey(type: InventoryTypeKey, currentVersion: CurrentVersionMap): string {
  return currentVersion[type] || getDefaultVersionKey(type);
}

const Step4: React.FC<Step4Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { openInventoryModal, isInventoryModalOpen, setOnInventoryUpdate, setCurrentVersions, setInventoryStatuses: setContextInventoryStatuses } = useInventoryModal();
  const { showConfirm, showAlert, showHelp } = useDialog();
  const { t } = useLocale();
  
  // Action system state
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: getStepActionLabel(4, 1),
      description: getStepActionDescription(4, 1),
      completed: false,
      hasButton: true,
      buttonText: STEP_UI.READ_GREEN_BOOK,
      buttonAction: () => setShowContentReader(true)
    },
    {
      id: 2,
      title: getStepActionLabel(4, 2),
      description: getStepActionDescription(4, 2),
      completed: false,
      buttons: [
        {
          text: STEP_UI.READ_GUIDANCE,
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: STEP_UI.WATCH_VIDEO,
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: getStepActionLabel(4, 3),
      description: getStepActionDescription(4, 3),
      completed: false
    },
    {
      id: 4,
      title: getStepActionLabel(4, 4),
      description: getStepActionDescription(4, 4),
      completed: false
    },
    {
      id: 5,
      title: getStepActionLabel(4, 5),
      description: getStepActionDescription(4, 5),
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  useRelocalizeStepActions(originalActions, setActions);
  
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationPopupType, setValidationPopupType] = useState<'action3'>('action3');
  const [action3Scheduled, setAction3Scheduled] = useState(false);
  const [action3Place, setAction3Place] = useState(false);

  // Inventory status tracking
  const [inventoryStatuses, setInventoryStatuses] = useState<Array<{
    type: string;
    labelKey: string;
    total: number;
    complete: number;
    isCompleted: boolean;
  }>>([]);

  // Existing inventory state
  const [auth, setAuth] = useState(false);
  const [currentType, setCurrentType] = useState('resentments');
  const [loading, setLoading] = useState(true);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();
  const [importStatus, setImportStatus] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [showContentReader, setShowContentReader] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);

  const [versions, setVersions] = useState<VersionMap>({ resentments: [], fears: [], harms: [] });
  const [currentVersion, setCurrentVersion] = useState<CurrentVersionMap>({
    resentments: getDefaultVersionKey('resentments'),
    fears: getDefaultVersionKey('fears'),
    harms: getDefaultVersionKey('harms'),
  });
  const [versionData, setVersionData] = useState<VersionDataMap>({});
  const versionDataRef = useRef(versionData);
  const currentVersionRef = useRef(currentVersion);
  versionDataRef.current = versionData;
  currentVersionRef.current = currentVersion;

  const getShareInventoryData = useCallback(() => {
    const filterItems = (key: string) =>
      (versionData[key] || []).filter((item) => item._type !== 'completion_status');
    return {
      resentments: filterItems(resolveInventoryVersionKey('resentments', currentVersion)),
      fears: filterItems(resolveInventoryVersionKey('fears', currentVersion)),
      harms: filterItems(resolveInventoryVersionKey('harms', currentVersion)),
    };
  }, [versionData, currentVersion]);

  const { openShareDialog, shareDialogProps } = useStepShareDialog(4, user, actions, getShareInventoryData);

  const applyLoadedStepProgress = async (
    stepProgress: ProgressItem | null,
    journeyProgress: JourneyProgress | null
  ) => {
    if (journeyProgress) {
      setIsOptedOut(journeyProgress.opted_out);
    }

    if (stepProgress) {
      setStepData(stepProgress);
      const savedData = stepProgress.data || {};

      if (savedData.actions) {
        const merged = mergeStepActions(4, originalActions, savedData, !!stepProgress.completed);
        setActions(merged.actions);

        const completedActionIds = merged.actions
          .filter((action) => action.completed)
          .map((action) => action.id);
        setCollapsedActions(new Set(completedActionIds));

        if (merged.migrationApplied) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 4,
            step_title: 'Step Four',
            completed: !!stepProgress.completed,
            data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
          });
        }
      }

      if (savedData.action3_scheduled !== undefined) {
        setAction3Scheduled(savedData.action3_scheduled);
      }
      if (savedData.action3_place !== undefined) {
        setAction3Place(savedData.action3_place);
      }
    }
  };

  const refreshStepData = async () => {
    if (!user) return;
    try {
      perfMark('Step4', 'refreshStepData start');
      const { steps, journeyProgress } = await perfTimeAsync('Step4 refreshStepData (total)', () =>
        progressService.loadStepPagesProgress('SAA', '12-steps', [4], { includeJourney: true })
      );
      await applyLoadedStepProgress(steps[4] ?? null, journeyProgress);
      perfSummary('Step Four page — progress refresh');
    } catch (err) {
      console.error('Error loading step progress:', err);
    }
  };

  // Load inventory data and step progress in parallel
  useEffect(() => {
    const initializeData = async (retryCount = 0): Promise<void> => {
      try {
        setLoading(true);
        clearError();

        if (!apiService.isAuthenticated()) {
          setAuth(false);
          setLoading(false);
          return;
        }

        setAuth(true);

        if (!user) {
          setLoading(false);
          return;
        }

        perfMark('Step4', 'initializeData start');
        const stepProgressPromise = perfTimeAsync('Step4 loadStepPagesProgress (parallel)', () =>
          progressService.loadStepPagesProgress('SAA', '12-steps', [4], { includeJourney: true })
        );

        const [resentmentVersions, fearVersions, harmVersions] = await Promise.all([
          InventoryService.listInventoryVersions('resentments'),
          InventoryService.listInventoryVersions('fears'),
          InventoryService.listInventoryVersions('harms'),
        ]);

        const newVersions: VersionMap = {
          resentments: resentmentVersions,
          fears: fearVersions,
          harms: harmVersions
        };

        setVersions(newVersions);

        // Set current versions (first available, or default key matching InventoryModal)
        const newCurrentVersion: CurrentVersionMap = {
          resentments: resentmentVersions.length > 0 ? resentmentVersions[0] : getDefaultVersionKey('resentments'),
          fears: fearVersions.length > 0 ? fearVersions[0] : getDefaultVersionKey('fears'),
          harms: harmVersions.length > 0 ? harmVersions[0] : getDefaultVersionKey('harms'),
        };

        setCurrentVersion(newCurrentVersion);

        // Load data for current versions (getInventory needs decryption - can fail if key not ready)
        const newVersionData: VersionDataMap = {};
        let anyLoadFailed = false;
        
        for (const type of INVENTORY_TYPE_KEYS) {
          const versionKey = newCurrentVersion[type];
          try {
            const items = await InventoryService.getInventory(type, versionKey);
            const filteredItems = items.filter(item => item._type !== 'completion_status');
            newVersionData[versionKey] = filteredItems;
          } catch (error) {
            // Don't silently treat as empty - may be encryption key timing
            anyLoadFailed = true;
            newVersionData[versionKey] = [];
          }
        }

        // Retry once if loads failed (likely encryption key not ready yet)
        if (anyLoadFailed && retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
          return initializeData(1);
        }

        setVersionData(newVersionData);

        const { steps, journeyProgress } = await stepProgressPromise;
        await applyLoadedStepProgress(steps[4] ?? null, journeyProgress);
        perfSummary('Step Four page');
        setLoading(false);

      } catch (error: any) {
        console.error('Error initializing data:', error);
        handleError(error, t('step.page.error.load'));
        setLoading(false);
      }
    };

    initializeData();
  }, [user, stepWorkspaceRevision]);

  // API wrapper functions - now using encrypted inventory service
  const saveInventoryToAPI = async (inventoryType: string, version: string, items: any[]) => {
    try {
      await InventoryService.saveInventory(inventoryType, version, items);
    } catch (error) {
      console.error('Error saving to API:', error);
      throw error;
    }
  };

  const deleteInventoryFromAPI = async (inventoryType: string, version: string) => {
    try {
      await InventoryService.deleteInventory(inventoryType, version);
    } catch (error) {
      console.error('Error deleting from API:', error);
      throw error;
    }
  };

  // Get current data for the selected version
  const getCurrentData = () => {
    return versionData[currentVersion[currentType]] || [];
  };

  // Check if there are no versions for the current inventory type
  const hasNoVersions = () => {
    return !versions[currentType] || versions[currentType].length === 0;
  };

  // Calculate counts for current inventory
  const getInventoryCounts = () => {
    const currentData = getCurrentData();
    const total = currentData.length;
    const complete = currentData.filter((item: any) => isComplete(currentType, item)).length;
    return { total, complete };
  };

  // Check if all three inventories have at least one entry
  const hasAllInventoriesStarted = () => {
    const resentmentsCount = versionData[resolveInventoryVersionKey('resentments', currentVersion)]?.length || 0;
    const fearsCount = versionData[resolveInventoryVersionKey('fears', currentVersion)]?.length || 0;
    const harmsCount = versionData[resolveInventoryVersionKey('harms', currentVersion)]?.length || 0;
    
    return resentmentsCount > 0 && fearsCount > 0 && harmsCount > 0;
  };

  // Calculate inventory statuses for all types
  const calculateInventoryStatuses = useCallback(async (forceRefreshData = false) => {
    const statuses = await Promise.all(INVENTORY_TYPES.map(async type => {
      const inventoryType = type.key as InventoryTypeKey;
      const versionKey = resolveInventoryVersionKey(inventoryType, currentVersionRef.current);
      
      // If forcing refresh, get fresh data from API
      let data: any[] = [];
      if (forceRefreshData && user) {
        try {
          const items = await InventoryService.getInventory(inventoryType, versionKey);
          data = items.filter(item => item._type !== 'completion_status');
        } catch (error) {
          console.error(`Error refreshing ${inventoryType} data:`, error);
          data = versionDataRef.current[versionKey] || [];
        }
      } else {
        data = versionDataRef.current[versionKey] || [];
      }
      
      const total = data.length;
      const complete = data.filter((item: any) => isComplete(inventoryType, item)).length;
      
      // Load completion status from API
      let isCompleted = false;
      if (user) {
        try {
          const completionData = await InventoryService.getInventoryCompletion(inventoryType, versionKey);
          isCompleted = completionData.completed || false;
        } catch (error) {
          // Silently handle errors
          isCompleted = false;
        }
      }
      
      const status = {
        type: type.key,
        labelKey: type.labelKey,
        total,
        complete,
        isCompleted
      };
      
      return status;
    }));
    
    setInventoryStatuses((prev) => {
      const prevKey = prev.map((s) => `${s.type}:${s.total}:${s.complete}:${s.isCompleted}`).join('|');
      const nextKey = statuses.map((s) => `${s.type}:${s.total}:${s.complete}:${s.isCompleted}`).join('|');
      return prevKey === nextKey ? prev : statuses;
    });
  }, [user]);

  const syncInventoriesFromApi = useCallback(async (inventoryType?: string) => {
    if (!user) return;

    const typesToSync = inventoryType
      ? INVENTORY_TYPES.filter((type) => type.key === inventoryType)
      : INVENTORY_TYPES;

    if (inventoryType) {
      try {
        const listedVersions = await InventoryService.listInventoryVersions(inventoryType);
        setVersions((prev) => ({ ...prev, [inventoryType]: listedVersions }));
      } catch (error) {
        console.error(`Error refreshing ${inventoryType} versions:`, error);
      }
    }

    const versionUpdates: VersionDataMap = {};
    await Promise.all(
      typesToSync.map(async (type) => {
        const inventoryTypeKey = type.key as InventoryTypeKey;
        const versionKey = resolveInventoryVersionKey(inventoryTypeKey, currentVersionRef.current);
        try {
          const items = await InventoryService.getInventory(inventoryTypeKey, versionKey);
          versionUpdates[versionKey] = items.filter((item) => item._type !== 'completion_status');
        } catch {
          versionUpdates[versionKey] = [];
        }
      })
    );

    if (Object.keys(versionUpdates).length > 0) {
      setVersionData((prev) => {
        const next = { ...prev, ...versionUpdates };
        versionDataRef.current = next;
        return next;
      });
    }

    await calculateInventoryStatuses(true);
  }, [user, calculateInventoryStatuses]);

  const syncInventoriesRef = useRef(syncInventoriesFromApi);
  syncInventoriesRef.current = syncInventoriesFromApi;

  // Refresh inventory status rows after initial load (and when current version changes).
  // Always show Resentments / Fears / Harms — even before any inventory versions exist.
  useEffect(() => {
    if (user && !loading) {
      void syncInventoriesRef.current();
    }
  }, [user, loading, currentVersion.resentments, currentVersion.fears, currentVersion.harms]);

  // Push status summary to modal context
  useEffect(() => {
    setContextInventoryStatuses(inventoryStatuses);
  }, [inventoryStatuses, setContextInventoryStatuses]);

  // Register modal update handler once (must use updater form — plain function arg is invoked by React)
  useEffect(() => {
    setOnInventoryUpdate(() => (inventoryType: string) => {
      void syncInventoriesRef.current(inventoryType);
    });
  }, [setOnInventoryUpdate]);

  useEffect(() => {
    setCurrentVersions(currentVersion);
  }, [setCurrentVersions, currentVersion]);

  const inventoryModalWasOpenRef = useRef(false);
  useEffect(() => {
    if (inventoryModalWasOpenRef.current && !isInventoryModalOpen) {
      void syncInventoriesRef.current();
    }
    inventoryModalWasOpenRef.current = isInventoryModalOpen;
  }, [isInventoryModalOpen]);

  // Handle Green Book button click
  const handleGreenBook = () => {
    setShowContentReader(true);
  };

  // Handle guidance button click
  const handleGuidanceClick = () => {
    setIsGuidanceOpen(true);
  };

  // Action system helper functions
  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    // For actions 1 and 2, require sequential completion
    if (actionIndex < 2) {
      const currentActionIndex = getCurrentActionIndex();
      return actionIndex <= currentActionIndex;
    }
    // Action 3 (inventories) is always available for checking/unchecking
    if (actionIndex === 3) {
      return true;
    }
    // Action 4 (share) and 5 (take step) require Action 3 to be completed
    if (actionIndex === 4 || actionIndex === 5) {
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true;
  };

  // Check if action 3 can be completed (all inventories must be completed AND both checkboxes checked)
  const canCompleteAction3 = () => {
    const allInventoriesCompleted = inventoryStatuses.every(status => status.isCompleted);
    const bothCheckboxesChecked = action3Scheduled && action3Place;
    return allInventoriesCompleted && bothCheckboxesChecked;
  };

  // Check if any action can be completed (for StepActions component)
  const canCompleteAction = (actionId: number) => {
    if (actionId === 3) {
      return canCompleteAction3();
    }
    if (actionId === 4 || actionId === 5) {
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true; // Actions 1 and 2 can always be completed
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      console.error('User not logged in. Please log in.');
      return;
    }

    try {
      // Special validation for action 3 - require all inventories to be completed AND both checkboxes checked
      const action3 = updatedActions.find(a => a.id === 3);
      if (action3 && action3.completed) {
        const allInventoriesCompleted = inventoryStatuses.every(status => status.isCompleted);
        const bothCheckboxesChecked = action3Scheduled && action3Place;
        
        if (!allInventoriesCompleted || !bothCheckboxesChecked) {
          setValidationPopupType('action3');
          setShowValidationPopup(true);
          return; // Don't update actions
        }
      }

      // Update local state
      setActions(updatedActions);

      // Save to backend with consistent structure, preserving checkbox data
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 4,
        step_title: 'Step Four',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(4).actionSchemaVersion, {
          action3_scheduled: action3Scheduled,
          action3_place: action3Place
        })
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }

    } catch (error: any) {
      console.error('Error updating action progress:', error);
      handleError(error, t('step.page.error.save_progress'));
      // Revert the action state on error
      setActions(prevActions => 
        prevActions.map(action => {
          const updatedAction = updatedActions.find(a => a.id === action.id);
          return updatedAction ? { ...action, completed: updatedAction.completed } : action;
        })
      );
    }
  };

  const handleActionButtonClick = (action: ActionItem, e: React.MouseEvent) => {
    e.preventDefault();
    if (action.buttonAction) {
      action.buttonAction();
    }
  };

  const toggleActionCollapse = (actionId: number) => {
    setCollapsedActions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(actionId)) {
        newSet.delete(actionId);
      } else {
        newSet.add(actionId);
      }
      return newSet;
    });
  };

  const handleAction3CheckboxChange = async (checkboxType: 'scheduled' | 'place') => {
    if (!user) {
      console.error('User not logged in. Please log in.');
      return;
    }

    const newValue = checkboxType === 'scheduled' ? !action3Scheduled : !action3Place;
    
    try {
      // Update the checkbox state
      if (checkboxType === 'scheduled') {
        setAction3Scheduled(newValue);
      } else {
        setAction3Place(newValue);
      }

      // Save to API with correct individual checkbox values
      await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 4,
        step_title: 'Step Four',
        completed: false,
        data: {
          action3_scheduled: checkboxType === 'scheduled' ? newValue : action3Scheduled,
          action3_place: checkboxType === 'place' ? newValue : action3Place
        }
      });

      // Note: Action 3 completion is now manual - user must click the "Completed" checkbox
      // Auto-completion has been removed to give users control over when they mark the step complete

    } catch (error) {
      console.error('Error updating action 3 checkbox:', error);
      // Revert the checkbox state on error
      if (checkboxType === 'scheduled') {
        setAction3Scheduled(!newValue);
      } else {
        setAction3Place(!newValue);
      }
    }
  };

  // Custom confirm dialog
  const openConfirm = (message: string, action: () => void, singleButton: boolean = false) => {
    if (singleButton) {
      showAlert({
        message,
        variant: 'info'
      });
    } else {
      showConfirm({
        message,
        onConfirm: action,
        variant: 'info'
      });
    }
  };
  
  // Show informational dialog (single button)
  const showInfo = (message: string) => {
    showAlert({
      message,
      variant: 'info'
    });
  };

  // Help dialog
  const openHelp = (fieldId: string) => {
    showHelp({
      title: t('step.4.inventory.help_dialog_title'),
      content: t('step.4.inventory.help_unavailable_field')
    });
  };

  // Breadcrumbs
  const breadcrumbs: BreadcrumbItem[] = getStepBreadcrumbs(4);

  if (!auth) {
    return (
      <div className={styles.step4Page}>
        <Header 
          breadcrumbs={breadcrumbs}
          user={user}
          onLogin={onLogin}
          onLogout={onLogout}
        />
        <main className={styles.mainContent}>
          <div className={styles.pageHeader}>
            <div className={styles.stepTitleContainer}>
              <h1 className={styles.stepTitle}>{getStepPageTitle(4)}</h1>
            <StepHelpButton stepKey="app" />
              {stepData && (
                <HistoryButton
                  stepNumber={4}
                  stepTitle={getStepPageTitle(4)}
                  stepData={stepData}
                  isOptedOut={isOptedOut}
                  onStepUpdated={() => setIsHistoryModalOpen(true)}
                />
              )}
            </div>
            <p className={styles.stepSubtitle}>{getStepPageQuote(4)}
            </p>
          </div>
          <StepLoginPrompt onLogin={onLogin} />
        </main>
      </div>
    );
  }



  return (
    <div className={styles.step4Page}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogin={onLogin}
        onLogout={onLogout}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>{getStepPageTitle(4)}</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={4}
                stepTitle={getStepPageTitle(4)}
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>{getStepPageQuote(4)}</p>
        </div>

        {loading && (
          <div className={styles.loadingMessage}>
            <p>{getStepLoadingMessage()}</p>
          </div>
        )}

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>{getRefreshPageLabel()}</button>
              </>
            )}
          </div>
        )}

        {!loading && !errorMessage && (
          <>
            {/* Action System */}
        <div style={{ marginBottom: '1rem' }}>
          <StepActions
            actions={withShareButton(4, actions, openShareDialog)}
            onActionChange={handleActionChange}
            stepNumber={4}
            canCompleteAction={canCompleteAction}
            renderActionContent={(action, index) => {
              if (action.id === 3) {
                const getStatusText = (status: any): string => {
                  if (status.total === 0) {
                    return t('step.4.inventory.status.not_started');
                  } else if (status.isCompleted) {
                    return t('step.4.inventory.status.completed', { count: status.total });
                  } else {
                    return t('step.4.inventory.status.in_progress', { count: status.total });
                  }
                };

                const getStatusColor = (status: any): string => {
                  if (status.total === 0) {
                    return '#9ca3af'; // gray
                  } else if (status.isCompleted) {
                    return '#10b981'; // green
                  } else {
                    return '#fbbf24'; // yellow
                  }
                };

                return (
                  <div className={`${styles.actionContentIndent} ${styles.action3Content}`}>
                    {/* Inventory Status List */}
                    <div className={styles.inventoryStatusList}>
                      {inventoryStatuses.map((status) => (
                        <div
                          key={status.type}
                          className={styles.inventoryStatusItem}
                          onClick={() => openInventoryModal(status.type)}
                          style={{ cursor: 'pointer' }}
                          role="button"
                          tabIndex={0}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openInventoryModal(status.type);
                            }
                          }}
                        >
                          <span className={styles.inventoryLabel}>
                            {t(status.labelKey)}
                          </span>
                          <span className={styles.inventoryStatus}>
                            <span
                              className={styles.statusBullet}
                              style={{ color: getStatusColor(status) }}
                            >
                              •
                            </span>
                            <span className={styles.statusText}>
                              {getStatusText(status)}
                            </span>
                            <span className={styles.chevronIcon}>→</span>
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Scheduling Section */}
                    <div className={styles.schedulingSection}>
                      <div className={styles.schedulingTitle}>
                        {t('step.4.inventory.schedule.title')}
                        <span className={styles.schedulingSubtext}>{t('step.4.inventory.schedule.subtext')}</span>
                      </div>
                      <div className={styles.schedulingCheckboxes}>
                        <label className={styles.schedulingCheckboxLabel}>
                          <input
                            type="checkbox"
                            checked={action3Scheduled}
                            onChange={() => handleAction3CheckboxChange('scheduled')}
                            className={styles.schedulingCheckbox}
                          />
                          <span className={styles.checkboxText}>
                            <span className={styles.checkboxTextFull}>{t('step.4.inventory.schedule.scheduled_full')}</span>
                            <span className={styles.checkboxTextMobile}>{t('step.4.inventory.schedule.scheduled_short')}</span>
                          </span>
                        </label>
                        <label className={styles.schedulingCheckboxLabel}>
                          <input
                            type="checkbox"
                            checked={action3Place}
                            onChange={() => handleAction3CheckboxChange('place')}
                            className={styles.schedulingCheckbox}
                          />
                          <span className={styles.checkboxText}>
                            <span className={styles.checkboxTextFull}>{t('step.4.inventory.schedule.place_full')}</span>
                            <span className={styles.checkboxTextMobile}>{t('step.4.inventory.schedule.place_short')}</span>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
        </div>

        {/* Congratulations Message */}
        {actions.every(action => action.completed) && (
          <>
            <div style={{ 
              backgroundColor: '#f0fff4', 
              border: '1px solid #9ae6b4', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#22543d' }}>{getCongratsHeading()}</h3>
              <p style={{ margin: 0, color: '#22543d' }}>
                {getCongratsBody(4)}
              </p>
            </div>
            <div className={styles.stepNavigation}>
              <div className={styles.navigationLinks}>
                <a href="/saa/12-steps/step-3" style={{ 
                  color: '#007bff',
                  textDecoration: 'none',
                  fontWeight: '500',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}>
                  {getPrevStepNavLabel(4)}
                </a>
                <span style={{ flex: 1 }}></span>
                <a href="/saa/12-steps/step-5" style={{ 
                  color: '#007bff',
                  textDecoration: 'none',
                  fontWeight: '500',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}>
                  {getNextStepNavLabel(4)}
                </a>
              </div>
            </div>
          </>
        )}

        {/* Navigation Links */}
        {!actions.every(action => action.completed) && (
          <div className={styles.stepNavigation}>
            <div className={styles.navigationLinks}>
              <a href="/saa/12-steps/step-3" style={{ 
                color: '#007bff',
                textDecoration: 'none',
                fontWeight: '500',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                background: '#f8f9fa',
                border: '1px solid #e9ecef'
              }}>
                {getPrevStepNavLabel(4)}
              </a>
              <span style={{ flex: 1 }}></span>
              <a href="/saa/12-steps/step-5" style={{ 
                color: '#007bff',
                textDecoration: 'none',
                fontWeight: '500',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                background: '#f8f9fa',
                border: '1px solid #e9ecef'
              }}>
                {getNextStepNavLabel(4)}
              </a>
            </div>
          </div>
        )}
          </>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={showContentReader}
        onClose={() => setShowContentReader(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-four start>>"
        endTag="<<step-four end>>"
      />
      
      {/* ContentReader for Step Four Guidance */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_4_Guidance.md"
        startTag="<<working-step-four start>>"
        endTag="<<working-step-four end>>"
        title={getGuidanceModalTitle(4)}
      />

      {/* Validation Popup Modal */}
      {showValidationPopup && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: 24, 
            borderRadius: 8, 
            maxWidth: 400, 
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>{t('step.4.inventory.validation.title')}</h3>
            <p style={{ margin: '0 0 24px 0', color: '#666', lineHeight: 1.4 }}>
              {validationPopupType === 'action3' 
                ? t('step.4.inventory.validation.action3')
                : t('step.4.inventory.validation.scheduling')
              }
            </p>
            <button
              onClick={() => setShowValidationPopup(false)}
              style={{ 
                background: '#007bff', 
                color: 'white', 
                border: 'none', 
                borderRadius: 4, 
                padding: '8px 24px', 
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {t('step.4.inventory.validation.ok')}
            </button>
          </div>
        </div>
      )}
      
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="AZGY_cEnF2Y"
        title={getVideoGuideTitle(4)}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={4}
        stepTitle={getStepPageTitle(4)}
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          if (user) {
            void refreshStepData();
          }
        }}
      />
      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step4; 
